import React, { useEffect, useState } from "react";
import { FlaskConical } from "lucide-react";
import { stratToday, stratTrials } from "../../lib/api";
import { AgentBrain } from "./AgentBrain";
import { ago } from "./useAgentStatus";

const tone = (s) => (s === "won" ? "text-emerald-300" : s === "lost" ? "text-rose-300" : "text-cyan-200/70");
const pnlOf = (t) => t.pnl_pct ?? t.last_pnl_pct;

export const useStrat = () => {
  const [d, setD] = useState({ today: [], trials: [], cands: [] });
  useEffect(() => {
    let dead = false;
    const pull = () => Promise.all([stratToday(), stratTrials()])
      .then(([a, b]) => !dead && setD({ today: a.items || [], trials: b.items || [], cands: b.candidates || [] })).catch(() => {});
    pull();
    const t = setInterval(pull, 60000);
    return () => { dead = true; clearInterval(t); };
  }, []);
  return d;
};

export const SlideStrat = () => {
  const { today, trials, cands } = useStrat();
  const closed = trials.filter((t) => t.status === "won" || t.status === "lost");
  const won = closed.filter((t) => t.status === "won").length;
  const open = trials.filter((t) => t.status === "open").length;
  return (
    <div className="h-full flex flex-col gap-2 min-h-0" data-testid="floor-slide-strat">
      <div className="flex items-center gap-2">
        <FlaskConical className="w-5 h-5 text-[#00F0FF]" />
        <p className="nx-orbitron text-sm font-bold text-[#00F0FF]">Mr Strat</p>
        <span className="fc-num text-[10px] text-cyan-300/50">Head of Strategy · reports to Sohel + Nexus · paper only</span>
        <span className="ml-auto fc-num text-[11px] text-cyan-50/85" data-testid="floor-strat-stats">
          {open} open · {won}W/{closed.length - won}L{closed.length ? ` · ${Math.round((won / closed.length) * 100)}%` : ""} · {cands.length} live-ready
        </span>
      </div>
      {today.length === 0 ? <p className="fc-num text-xs text-cyan-100/50">No strategies yet today — next run 5am CT.</p> : today.map((s) => (
        <div key={s.id} className="rounded-xl border border-cyan-500/30 bg-cyan-500/[0.04] px-3 py-2" data-testid={`floor-strat-today-${s.author}`}>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[9px] font-black tracking-widest text-[#030712] bg-[#00F0FF] rounded px-1.5 py-0.5 shrink-0">{s.author === "mr_strat" ? "MR STRAT" : "NEXUS"}</span>
            <span className="font-bold text-cyan-50 text-sm truncate">{s.name}</span>
            <span className={`ml-auto fc-num text-xs tabular-nums ${tone(s.status)}`}>{s.symbol} {s.direction} · {pnlOf(s) != null ? `${pnlOf(s)}%` : "—"}</span>
          </div>
          <p className="fc-num text-[10px] text-cyan-50/75 leading-tight line-clamp-2 mt-1">{s.thesis}</p>
        </div>
      ))}
      <div className="flex-1 min-h-0 overflow-hidden" data-testid="floor-strat-trials">
        {trials.slice(0, 8).map((t) => (
          <div key={t.id} className="flex items-center gap-2 fc-num text-[11px] border-t border-white/5 py-1">
            <span className="text-cyan-50 truncate flex-1">{t.name}{t.candidate ? " ★" : ""}</span>
            <span className="text-cyan-300/50 w-16">{t.author === "mr_strat" ? "Mr Strat" : "Nexus"}</span>
            <span className={`capitalize w-12 ${tone(t.status)}`}>{t.status}</span>
            <span className={`w-14 text-right tabular-nums ${tone(t.status)}`}>{pnlOf(t) != null ? `${pnlOf(t)}%` : "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const StratBox = () => {
  const { today, trials } = useStrat();
  const last = trials[0];
  const age = last?.marked_at || last?.created_at ? (Date.now() - Date.parse(last.marked_at || last.created_at)) / 60000 : null;
  const mine = today.find((t) => t.author === "mr_strat");
  const open = trials.filter((t) => t.status === "open").length;
  return (
    <div className="relative min-h-0 rounded-xl border p-2.5 flex flex-col gap-1 overflow-hidden" style={{ borderColor: "#00F0FF55", background: "#00F0FF0a" }} data-testid="floor-agent-box-mr_strat">
      <div className="flex items-center gap-2 min-w-0">
        <AgentBrain agent="mr_strat" size={34} active={age != null && age < 16} />
        <div className="min-w-0">
          <p className="nx-orbitron text-xs font-bold truncate text-[#00F0FF]">Mr Strat</p>
          <p className="fc-num text-[9px] text-cyan-300/50 truncate">Head of Strategy</p>
        </div>
      </div>
      <p className="fc-num text-[10px] text-cyan-50/85 leading-tight line-clamp-2" data-testid="floor-agent-task-mr_strat">
        {mine ? `Testing ${mine.name} · ${open} paper trials open` : "Next strategy at 5am CT"}
      </p>
      <p className="fc-num text-[9px] text-cyan-300/50 mt-auto" data-testid="floor-agent-ago-mr_strat">last active {ago(age)}</p>
    </div>
  );
};
